import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Layout, Typography, Table, Button, Card, Tag, Form, Row, Col, DatePicker } from 'antd';
import { LoadingOutlined, MenuOutlined, FileTextOutlined, UserOutlined, LogoutOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import '../styles/ScholarAcknowledgementReceipt.css';
import '../styles/SharedLayout.css';
import '../styles/MobileMenuShared.css';
import ScholarSidebar from '../components/ScholarSidebar';
import { getRequest } from '../utilities/apicalls';
import ToastMessage from '../components/ToastMessage';

// Destructure components
const { Content } = Layout;
const { Title, Text } = Typography;

/**
 * ScholarAcknowledgementReceipt Component
 * 
 * Displays the acknowledgement receipts for a scholar with filtering capabilities
 * @returns {JSX.Element} The ScholarAcknowledgementReceipt component
 */
const ScholarAcknowledgementReceipt = () => {
    // Update state for date range instead of single date
    const [dateRange, setDateRange] = useState([null, null]);
    const [form] = Form.useForm();
    
    // State for data and loading
    const [filteredReceipts, setFilteredReceipts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pageSize, setPageSize] = useState(10);
    
    // State for mobile sidebar visibility
    const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
    
    // Router hooks
    const history = useHistory();
    
    // Toggle mobile sidebar
    const toggleMobileSidebarVisibility = () => {
        setMobileSidebarVisible(!mobileSidebarVisible);
    };
    
    // Handle mobile menu item clicks
    const handleMobileMenuClick = (path) => {
        setMobileSidebarVisible(false);
        history.push(path);
    };
    
    // Mobile menu items
    const mobileMenuItems = [
        {
            key: 'acknowledgement-receipt',
            icon: <FileTextOutlined />,
            label: 'AR',
            onClick: () => handleMobileMenuClick('/scholar-acknowledgement-receipt')
        },
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profile',
            onClick: () => handleMobileMenuClick('/scholar-profile')
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            onClick: () => handleMobileMenuClick('/scholar-logout')
        }
    ];
    
    // Fetch receipt data from API
    const fetchReceipts = useCallback(async (dateFrom = null, dateTo = null) => {
        setLoading(true);
        setError(null);
        
        try {
            // Get authentication data from localStorage
            const token = localStorage.getItem('token');
            const id = localStorage.getItem('id');
            const tokenExpiry = localStorage.getItem('tokenExpiry');
            
            if (!token || !id) {
                throw new Error('Authentication data not found. Please log in again.');
            }
            
            // Check if token is expired
            if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
                // Token expired, redirect to login
                localStorage.removeItem('token');
                localStorage.removeItem('id');
                localStorage.removeItem('tokenExpiry');
                ToastMessage.error('Your session has expired. Please log in again.');
                history.push('/login');
                return;
            }
            
            // Prepare API params with optional date range
            const params = {
                scholar_id: id,
                screen: 'scholar',
                token: token,
                requester: id
            };
            
            // Add date range to params if provided
            if (dateFrom && dateTo) {
                params.date_from = dateFrom.format('YYYY-MM-DD');
                params.date_to = dateTo.format('YYYY-MM-DD');
            }
            
            // Make API call with GET method
            const response = await getRequest('receipts/get', params);
            
            // Initialize empty arrays for state
            let formattedReceipts = [];
            
            // Handle the response data
            if (response.data) {
                let dataToProcess = response.data;
                
                // Convert single object to array if needed
                if (!Array.isArray(dataToProcess)) {
                    if (dataToProcess.data) {
                        // If response has a data property
                        dataToProcess = Array.isArray(dataToProcess.data) ? dataToProcess.data : [dataToProcess.data];
                    } else {
                        // If response is a single object
                        dataToProcess = [dataToProcess];
                    }
                }
                
                // Filter out any null or undefined items
                dataToProcess = dataToProcess.filter(item => item != null);
                
                if (dataToProcess.length > 0) {
                    formattedReceipts = dataToProcess.map(item => ({
                        id: item.id || item.receipt_id || Math.random().toString(36).substring(2),
                        date: item.date_received || item.date || 
                              (item.created_at ? dayjs(item.created_at).format('MMMM D, YYYY') : dayjs().format('MMMM D, YYYY')),
                        type: item.description || item.type || item.receipt_type || 'Monthly Allowance',
                        declaration: item.declaration || 
                                   `I hereby acknowledge the receipt of my scholarship allowance for ${dayjs(item.date_received || item.created_at || new Date()).format('MMMM D, YYYY')}. I declare that all information provided is true and correct to the best of my knowledge.`,
                        signature: item.updated_on ? dayjs(item.updated_on).format('MMM DD, YYYY h:mm A') : '',
                        status: item.status || (item.signature ? 'Signed' : 'Pending'),
                        amount: item.amount || 'N/A',
                        scholarDetails: {
                            name: item.scholar_name || item.name || 'N/A',
                            email: item.scholar_email || item.email || 'N/A',
                            course: item.course || 'N/A',
                            scholarType: item.scholar_type || item.type || 'N/A'
                        },
                        signatureImage: item.signature || null
                    }));
                }
            }
            
            // Update state based on results
            setFilteredReceipts(formattedReceipts);
            
            // Set appropriate message if no receipts found
            if (formattedReceipts.length === 0) {
                setError('No acknowledgement receipt records found');
            } else {
                setError(null);
            }
            
        } catch (err) {
            // console.error('Fetch receipts error:', err);
            ToastMessage.error('Failed to fetch receipts');
        } finally {
            setLoading(false);
        }
    }, [history]);

    // Initialize filtered receipts on component mount
    useEffect(() => {
        fetchReceipts();
        
        // Cleanup function for when component unmounts
        return () => {
            // Any cleanup needed
        };
    }, [fetchReceipts]);

    // Filter handlers - updated for date range
    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        form.setFieldsValue({ dateRange: dates });
        
        // Automatically apply filter when date range changes
        if (dates && dates[0] && dates[1]) {
            const startDate = dates[0].startOf('day');
            const endDate = dates[1].endOf('day');
            
            // Fetch data from API with date range filter
            fetchReceipts(startDate, endDate);
        } else {
            // If date range is cleared or incomplete, reset filters and fetch all receipts
            fetchReceipts();
        }
    };
    
    // Handle retry when initial fetch fails
    const handleRetry = () => {
        fetchReceipts();
    };

    // Wrap handleViewDetails with useCallback
    const handleViewDetails = useCallback((receipt) => {
        history.push({
            pathname: `/scholar-acknowledgement-details/${receipt.id}`,
            state: { 
                transaction: {
                    ...receipt,
                    signature: receipt.signatureImage // Ensure signature is included
                }
            }
        });
    }, [history]);

    // Memoize the columns to prevent unnecessary re-renders
    const columns = useMemo(() => [
        {
            title: 'Date Received',
            dataIndex: 'date',
            key: 'date',
            align: 'left',
            titleAlign: 'left',
            onHeaderCell: () => ({
                style: { textAlign: 'left' }
            }),
            width: 200,
            render: (text) => {
                try {
                    const dateObj = dayjs(text);
                    return dateObj.format('MMMM D, YYYY');
                } catch (error) {
                    return text || 'Unknown date';
                }
            }
        },
        {
            title: 'Description',
            dataIndex: 'type',
            key: 'type',
            align: 'left',
            titleAlign: 'left',
            onHeaderCell: () => ({
                style: { textAlign: 'left' }
            }),
            render: (text) => text
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'left',
            titleAlign: 'left',
            onHeaderCell: () => ({
                style: { textAlign: 'left' }
            }),
            render: (amount) => amount ? `₱${parseFloat(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'
        },
        {
            title: 'Signed on',
            dataIndex: 'signature',
            key: 'signature',
            align: 'left',
            titleAlign: 'left',
            onHeaderCell: () => ({
                style: { textAlign: 'left' }
            }),
            render: (text) => {
                if (!text) return '';
                
                try {
                    const date = dayjs(text, 'MMM DD, YYYY h:mm A');
                    if (date.isValid()) {
                        return text;
                    }
                    return '';
                } catch (error) {
                    return '';
                }
            }
        },
        {
            title: <div style={{ textAlign: 'center', width: '100%', display: 'flex', justifyContent: 'center' }}>Status</div>,
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: 120,
            onHeaderCell: () => ({
                style: { 
                    textAlign: 'center', 
                    justifyContent: 'center'
                }
            }),
            render: (status) => {
                const normalizedStatus = String(status || '').trim().toLowerCase();
                
                if (normalizedStatus === 'signed') {
                    return <Tag color="success">Signed</Tag>;
                } else if (normalizedStatus === 'for signature') {
                    return <Tag color="orange">For Signature</Tag>;
                } else {
                    return <Tag color="default">{status}</Tag>;
                }
            }
        }
    ], []);

    return (
        <div className="page-wrapper">
            <Layout className='page-body'>
                {/* Mobile Toggle Button and Menu */}
                <div className="mobile-menu-container">
                    <Button
                        type="primary"
                        className="mobile-sidebar-toggle"
                        icon={<MenuOutlined />}
                        onClick={toggleMobileSidebarVisibility}
                        aria-label="Toggle navigation menu"
                        shape="circle"
                        size="large"
                    />
                    
                    {/* Mobile Menu Popup */}
                    <div className={`mobile-menu-popup ${mobileSidebarVisible ? 'visible' : ''}`}>
                        {mobileMenuItems.map(item => (
                            <Button 
                                key={item.key}
                                className={`mobile-menu-item ${item.key === 'acknowledgement-receipt' ? 'active' : ''}`}
                                icon={item.icon}
                                onClick={item.onClick}
                                type="text"
                                block
                            >
                                {item.label}
                            </Button>
                        ))}
                    </div>
                </div>
                
                {/* Mobile Menu Overlay */}
                <div 
                    className={`mobile-menu-overlay ${mobileSidebarVisible ? 'visible' : ''}`}
                    onClick={() => setMobileSidebarVisible(false)}
                />
                
                {/* Regular Sidebar (only visible on desktop) */}
                <ScholarSidebar 
                    activePage="acknowledgement-receipt" 
                />
                
                <Content className='main-content' style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '24px' }}>
                    {/* Header */}
                    <div className='scholar-sar-header' style={{ textAlign: 'left', margin: '0', maxWidth: '1200px', paddingLeft: '0', alignSelf: 'flex-start', width: '100%' }}>
                        <Title level={3} className='scholar-sar-title' style={{ textAlign: 'left', paddingLeft: '0' }}>Acknowledgement Receipts</Title>
                    </div>

                    {/* Filters Card */}
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignSelf: 'flex-start' }}>
                        <Card className='scholar-sar-filters-card' style={{ maxWidth: '1200px', margin: '0 0 16px 0', width: '100%', alignSelf: 'flex-start' }}>
                            <Form 
                                form={form}
                                initialValues={{ dateRange: dateRange }}
                                layout="vertical"
                                className="scholar-sar-filter-form"
                            >
                                <Row gutter={16} align="middle" justify="start">
                                    <Col xs={24} sm={16} md={12} lg={10}>
                                        <Form.Item 
                                            name="dateRange" 
                                            className="scholar-sar-form-item"
                                            rules={[
                                                {
                                                    required: false,
                                                    message: 'Please select a date range'
                                                }
                                            ]}
                                        >
                                            <DatePicker.RangePicker 
                                                onChange={handleDateRangeChange}
                                                format="YYYY-MM-DD"
                                                className="scholar-sar-date-range-picker"
                                                disabled={loading}
                                                allowClear={true}
                                                allowEmpty={[true, true]}
                                                placeholder={['Date from', 'Date to']}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>
                    </div>

                    {/* Receipt Table */}
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <Card className="scholar-sar-table-card" style={{ maxWidth: '1200px', margin: '0 0 16px 0', width: '100%', alignSelf: 'flex-start' }}>
                            <Table 
                                columns={columns} 
                                dataSource={filteredReceipts}
                                rowKey="id"
                                loading={loading ? { indicator: <LoadingOutlined style={{ fontSize: 24 }} spin /> } : false}
                                pagination={{
                                    defaultPageSize: 10,
                                    pageSize: pageSize,
                                    total: filteredReceipts.length,
                                    showSizeChanger: true,
                                    pageSizeOptions: [5, 10, 20, filteredReceipts.length.toString()],
                                    responsive: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                    position: ['bottomCenter'],
                                    onShowSizeChange: (current, size) => {
                                        // Reset to first page when changing page size
                                        setPageSize(size);
                                    },
                                    itemRender: (page, type, element) => {
                                        if (type === 'page') {
                                            return <button type="button" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>{page}</button>;
                                        }
                                        return element;
                                    }
                                }}
                                className="scholar-sar-receipt-table scholars-table"
                                bordered={false}
                                size="middle"
                                onRow={(record) => ({
                                    onClick: () => handleViewDetails(record),
                                    style: { cursor: 'pointer' },
                                    className: 'scholar-row'
                                })}
                                style={{
                                    borderCollapse: 'collapse'
                                }}
                                components={{
                                    header: {
                                        cell: (props) => <th {...props} style={{ borderBottom: '1px solid #f0f0f0', borderRight: 'none' }} />
                                    },
                                    body: {
                                        cell: (props) => <td {...props} style={{ borderBottom: '1px solid #f0f0f0', borderRight: 'none' }} />
                                    }
                                }}
                                locale={{ 
                                    emptyText: error ? (
                                        <div style={{ padding: '20px 0' }}>
                                            <p>{error}</p>
                                            <Button onClick={handleRetry} type="primary">
                                                Retry
                                            </Button>
                                            {(dateRange && dateRange[0] && dateRange[1]) ? (
                                                <small>Try removing date filters or adjusting your search criteria</small>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <div style={{ padding: '20px 0' }}>
                                            <p>No acknowledgement receipts found</p>
                                            <small>No records available at this time</small>
                                        </div>
                                    ),
                                    Pagination: {
                                        items_per_page: '/ page',
                                        jump_to: 'Go to',
                                        page: 'Page',
                                        prev_page: 'Previous Page',
                                        next_page: 'Next Page',
                                        prev_5: 'Previous 5 Pages',
                                        next_5: 'Next 5 Pages'
                                    }
                                }}
                            />
                        </Card>
                    </div>

                    {/* Company Logo */}
                    <div className="scholar-sar-company-branding">
                        <Text className="scholar-sar-company-text">©2025 MYT Softdev Solutions Inc.</Text>
                    </div>
                </Content>
            </Layout>
        </div>
    );
};

export default ScholarAcknowledgementReceipt; 